import { useCallback, useRef, useState } from 'react';
import { usePendingChangesStore } from '@/store/pendingChangesStore';
import type { DispatchedKeys } from '@/store/pendingChangesStore';
import { useCampaignSettingsGoogleAdsStore } from '@/store/campaignSettingsGoogleAdsStore';
import { useCampaignSettingsMetaAdsStore } from '@/store/campaignSettingsMetaAdsStore';
import { useAccountSettingsStore } from '@/store/accountSettingsStore';
import { saveAppConfig } from '@/services/appConfigService';

/**
 * Custom hook for managing combined changes on the Account page.
 *
 * Combines changes from four sources:
 * 1. pendingChangesStore - Campaign status changes (pause/enable/remove)
 * 2. campaignSettingsGoogleAdsStore - Per-campaign budget, name, optimization toggle
 * 3. campaignSettingsMetaAdsStore - Per-Meta-campaign budget, name, optimization toggle
 * 4. accountSettingsStore - Account-level total budget, ROAS, target/min density
 *
 * Returns functions compatible with SaveChangesBar's extended mode props.
 */
export function useAccountChanges() {
  const {
    hasChanges: hasPendingChanges,
    getChangeCount: getPendingChangeCount,
    getChangeSummary: getPendingChangeSummary,
    isSaving: isPendingSaving,
    saveAllChanges: savePendingChanges,
    clearAllChanges: clearPendingChanges,
    executeDiscardCallbacks,
  } = usePendingChangesStore();

  const manualBudgetChanges = useCampaignSettingsGoogleAdsStore((state) => state.manualBudgetChanges);
  const manualNameChanges = useCampaignSettingsGoogleAdsStore((state) => state.manualNameChanges);
  const optimizationToggleChanges = useCampaignSettingsGoogleAdsStore((state) => state.optimizationToggleChanges);
  const discardGoogleChanges = useCampaignSettingsGoogleAdsStore((state) => state.discardChanges);
  // Phase 1b — Meta-side dirty sets join the same change-count + summary
  // pipeline so the SaveChangesBar shows a single "5 changes pending"
  // total across both platforms instead of dropping the Meta half.
  const metaManualBudgetChanges = useCampaignSettingsMetaAdsStore((state) => state.manualBudgetChanges);
  const metaManualNameChanges = useCampaignSettingsMetaAdsStore((state) => state.manualNameChanges);
  const metaOptimizationToggleChanges = useCampaignSettingsMetaAdsStore((state) => state.optimizationToggleChanges);
  const discardMetaChanges = useCampaignSettingsMetaAdsStore((state) => state.discardChanges);

  const hasTotalBudgetChange = useAccountSettingsStore((state) => state.hasTotalBudgetChange);
  const hasRoasSettingsChange = useAccountSettingsStore((state) => state.hasRoasSettingsChange);
  const hasTargetDensityChange = useAccountSettingsStore((state) => state.hasTargetDensityChange);
  const hasMinDensityChange = useAccountSettingsStore((state) => state.hasMinDensityChange);
  const hasTargetAbsDensityChangeRateChange = useAccountSettingsStore(
    (state) => state.hasTargetAbsDensityChangeRateChange,
  );
  const discardAccountChanges = useAccountSettingsStore((state) => state.discardChanges);

  const [isSaving, setIsSaving] = useState(false);
  // Re-entrance guard on onDiscard. Without it, a single Discard click
  // observed in a smoketest cascaded into an infinite loop — each
  // discard call's downstream store updates (Google + Meta + account
  // settings reset, optimistic state cleared) triggered enough
  // re-renders that something downstream invoked onDiscard again,
  // re-firing the cascade. The root re-entrance trigger is hard to
  // pin without a stack trace, but the guard is cheap and correct
  // either way: onDiscard's effects are all sync stores writes, so a
  // recursive call within the same render commit can only redo work
  // that's already been done.
  const isDiscardingRef = useRef(false);

  // Calculate if there are any changes from either store
  const pendingStatusChanges = hasPendingChanges();
  const hasBudgetOrNameChanges =
    manualBudgetChanges.size > 0
    || manualNameChanges.size > 0
    || metaManualBudgetChanges.size > 0
    || metaManualNameChanges.size > 0;
  const hasOptimizationChanges =
    optimizationToggleChanges.size > 0
    || metaOptimizationToggleChanges.size > 0;
  const hasAccountSettingsChanges =
    hasTotalBudgetChange
    || hasRoasSettingsChange
    || hasTargetDensityChange
    || hasMinDensityChange
    || hasTargetAbsDensityChangeRateChange;

  const hasChanges = useCallback(() => {
    return pendingStatusChanges || hasBudgetOrNameChanges || hasOptimizationChanges || hasAccountSettingsChanges;
  }, [pendingStatusChanges, hasBudgetOrNameChanges, hasOptimizationChanges, hasAccountSettingsChanges]);

  const getChangeCount = useCallback(() => {
    return (
      getPendingChangeCount() +
      manualBudgetChanges.size +
      manualNameChanges.size +
      optimizationToggleChanges.size +
      metaManualBudgetChanges.size +
      metaManualNameChanges.size +
      metaOptimizationToggleChanges.size +
      (hasTotalBudgetChange ? 1 : 0) +
      (hasRoasSettingsChange ? 1 : 0) +
      (hasTargetDensityChange ? 1 : 0) +
      (hasMinDensityChange ? 1 : 0) +
      (hasTargetAbsDensityChangeRateChange ? 1 : 0)
    );
  }, [getPendingChangeCount, manualBudgetChanges.size, manualNameChanges.size, optimizationToggleChanges.size, metaManualBudgetChanges.size, metaManualNameChanges.size, metaOptimizationToggleChanges.size, hasTotalBudgetChange, hasRoasSettingsChange, hasTargetDensityChange, hasMinDensityChange, hasTargetAbsDensityChangeRateChange]);

  const getChangeSummary = useCallback(() => {
    const summary = [...getPendingChangeSummary()];
    if (hasTotalBudgetChange) {
      summary.push('Allocatable budget update');
    }
    if (optimizationToggleChanges.size > 0) {
      summary.push(`${optimizationToggleChanges.size} Google budget optimization update`);
    }
    if (manualBudgetChanges.size > 0) {
      summary.push(`${manualBudgetChanges.size} Google budget update(s)`);
    }
    if (manualNameChanges.size > 0) {
      summary.push(`${manualNameChanges.size} Google name update(s)`);
    }
    if (metaOptimizationToggleChanges.size > 0) {
      summary.push(`${metaOptimizationToggleChanges.size} Meta budget optimization update`);
    }
    if (metaManualBudgetChanges.size > 0) {
      summary.push(`${metaManualBudgetChanges.size} Meta budget update(s)`);
    }
    if (metaManualNameChanges.size > 0) {
      summary.push(`${metaManualNameChanges.size} Meta name update(s)`);
    }
    if (hasRoasSettingsChange) {
      summary.push('ROAS calculation settings');
    }
    if (hasTargetDensityChange) {
      summary.push('Target density');
    }
    if (hasMinDensityChange) {
      summary.push('Min density');
    }
    if (hasTargetAbsDensityChangeRateChange) {
      summary.push('Target density change rate');
    }
    return summary;
  }, [getPendingChangeSummary, hasTotalBudgetChange, optimizationToggleChanges.size, manualBudgetChanges.size, manualNameChanges.size, metaOptimizationToggleChanges.size, metaManualBudgetChanges.size, metaManualNameChanges.size, hasRoasSettingsChange, hasTargetDensityChange, hasMinDensityChange, hasTargetAbsDensityChangeRateChange]);

  const onSave = useCallback(async (): Promise<{
    success: boolean;
    errors: string[];
    dispatched?: DispatchedKeys;
  }> => {
    setIsSaving(true);
    const errors: string[] = [];
    // Captured from the pendingChangesStore save (when it runs) and
    // forwarded up to SaveChangesBar so it can target the post-save
    // clear to just these keys. When the user's only edits live outside
    // the pendingChangesStore (account-settings, manual budget/name
    // edits routed through saveAppConfig), `dispatched` stays undefined
    // and SaveChangesBar skips the targeted clear — the per-store
    // discard / commit logic already handled those.
    let dispatched: DispatchedKeys | undefined;

    try {
      // 1. Save pending status changes (Google Ads API calls)
      // Stop on first failure to prevent partial saves with inconsistent state
      if (pendingStatusChanges) {
        const result = await savePendingChanges();
        if (!result.success) {
          errors.push(...result.errors);
          // Return early - don't proceed with config changes if status changes failed
          return { success: false, errors };
        }
        dispatched = result.dispatched;
      }

      // 2. Save campaign-level (budget/name/optimization toggle) AND
      // account-level (total budget, ROAS, target/min density) changes
      // via the orchestrator. Each registered slice provider's preSave
      // runs first (Google flushes its dedicated rename/budget endpoints),
      // then slices are gathered and posted atomically. Adding a third
      // platform is just registering another slice provider — no change
      // needed here.
      if (hasBudgetOrNameChanges || hasOptimizationChanges || hasAccountSettingsChanges) {
        try {
          await saveAppConfig();
        } catch (error) {
          errors.push(error instanceof Error ? error.message : 'Failed to save configuration changes');
          return { success: false, errors };
        }
      }

      return { success: true, errors: [], ...(dispatched && { dispatched }) };
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      return { success: false, errors: [errorMsg] };
    } finally {
      setIsSaving(false);
    }
  }, [pendingStatusChanges, hasBudgetOrNameChanges, hasOptimizationChanges, hasAccountSettingsChanges, savePendingChanges]);

  const onDiscard = useCallback(() => {
    // Re-entrance guard. Without it, downstream re-renders triggered
    // by these store writes can cycle back into onDiscard before the
    // first invocation finishes its work, producing an infinite
    // discard loop. The guard lets the first call complete and
    // silently no-ops nested calls.
    if (isDiscardingRef.current) {
      // Diagnostic — log the stack of the re-entrant caller so we
      // can localize the trigger. The earlier idempotency fix to the
      // Meta store's `initializeCampaignSettings` didn't break the
      // loop, so the cycle source isn't refetch-identical-data
      // churn. Next reproduce will tell us. Dev-only — production
      // gets silent no-op.
      if (import.meta.env.DEV) {
        console.warn('[onDiscard] re-entrant call detected — guarded. Stack:');
        console.trace('onDiscard re-entrant');
      }
      return;
    }
    isDiscardingRef.current = true;
    try {
      // Execute all registered discard callbacks to restore component state
      executeDiscardCallbacks();
      // Clear pending changes from the queue
      clearPendingChanges();
      // Discard in-flight per-campaign edits (Google + Meta) and account-level
      // edits. pendingChangesStore's clear above handles the queued items;
      // these calls clear the per-store local manual fields (manualName,
      // manualBudget, nameValidationError) that aren't in the queue.
      discardGoogleChanges();
      discardMetaChanges();
      discardAccountChanges();
    } finally {
      // Hold the guard through React's commit + effects phase. The
      // earlier `queueMicrotask` released too early — by the time
      // a useEffect ran in response to the store writes above and
      // (somehow) re-invoked onDiscard, the flag was already false
      // and the loop slipped through. 100ms is well past the typical
      // React commit + effect window on this app.
      setTimeout(() => {
        isDiscardingRef.current = false;
      }, 100);
    }
  }, [executeDiscardCallbacks, clearPendingChanges, discardGoogleChanges, discardMetaChanges, discardAccountChanges]);

  return {
    hasChanges,
    getChangeCount,
    getChangeSummary,
    onSave,
    onDiscard,
    isExternallySaving: isSaving || isPendingSaving,
  };
}
